<script setup>
import filterUrlTransformer from "~/utils/FilterUrlTransformer";
import { scrollToTop } from "~/utils/scrollToTop";
const { $get } = useAxios();

const moreButtonClickCount = useCookie(COOKIE_KEYS.moreButtonClickCount);
const perPage = 30;
const isMoreDetails = ref(false);
const page = computed({
  get() {
    return parseInt(route.query.page ?? "1");
  },
  async set(value) {
    moreButtonClickCount.value = 1;
    isMoreDetails.value = false;
    await navigateTo(
      `${link.value}&sort=${sort.value}&mode=${mode.value}&page=${value}`
    );
    scrollToTop();
  },
});

async function more() {
  moreButtonClickCount.value += 1;
  isMoreDetails.value = true;
  await navigateTo(
    `${link.value}&sort=${sort.value}&mode=${mode.value}&page=${page.value + 1}`
  );
}
const sort = computed({
  get() {
    return (
      filterUrlTransformer.parseEnumSort(route.query?.sort ?? "") ??
      SortType.PRICE_ASC
    );
  },
  async set(value) {
    await navigateTo(
      `${link.value}&sort=${value}&mode=${mode.value}&page=${page.value}`
    );
  },
});

const mode = computed({
  get() {
    return route.query?.mode ?? "tile";
  },
  async set(value) {
    await navigateTo(`${link.value}&mode=${value}&sort=${sort.value}`);
  },
});
const link = computed(() => {
  let filter =
    storeFilters.value?.filter && storeFilters.value?.filter.size
      ? filterUrlTransformer.transformAttrMapToUrl(storeFilters.value?.filter)
      : null;

  let filterRange =
    storeFilters.value?.range && storeFilters.value?.range.size
      ? filterUrlTransformer.transformAttrMapToUrl(storeFilters.value?.range)
      : null;

  return `${route.path}?${[
    ["filter", filter],
    ["range", filterRange],
    ["minPrice", storeFilters.value.minPrice],
    ["maxPrice", storeFilters.value.maxPrice],
  ]
    .filter((entry) => !!entry[1])
    .map((entry) => entry.join("="))
    .join("&")}`;
});

const route = useRoute();

const drawerVisible = ref(false);

const storeFilters = ref({});

const restoreFilters = () => {
  // storeFilters.value.search = route.query?.search
  storeFilters.value.minPrice = filterUrlTransformer.parseNumber(
    route.query?.minPrice ?? ""
  );
  storeFilters.value.maxPrice = filterUrlTransformer.parseNumber(
    route.query?.maxPrice ?? ""
  );
  storeFilters.value.filter = filterUrlTransformer.parseFilters(
    route.query?.filter ?? ""
  );
  storeFilters.value.range = filterUrlTransformer.parseFilters(
    route.query?.range ?? ""
  );
};
restoreFilters();

const storeFiltersValues = computed({
  get() {
    let data = [];

    if (storeFilters.value?.filter.size) {
      storeFilters.value?.filter.forEach((value, key) => {
        data.push({
          filterId: key,
          isRange: false,
          values: value,
        });
      });
    }

    if (storeFilters.value?.range.size) {
      storeFilters.value?.range.forEach((value, key) => {
        data.push({
          filterId: key,
          isRange: true,
          values: value,
        });
      });
    }
    return data;
  },
  set(value) {
    const attributeMap = new Map();
    const attributeRangeMap = new Map();
    if (value && value.length) {
      value.forEach((attr) => {
        if (attr.isRange) {
          attributeRangeMap.set(attr.filterId, attr.values);
        } else {
          attributeMap.set(attr.filterId, attr.values);
        }
      });
    }
    storeFilters.value.filter = attributeMap;
    storeFilters.value.range = attributeRangeMap;
  },
});

function changeMode(value) {
  mode.value = value;
}

function changeSort(value) {
  sort.value = value.sort;
}

const limit = computed(() => {
  if (isMoreDetails.value) {
    return moreButtonClickCount.value * perPage;
  } else {
    return perPage;
  }
});

const {
  data: products,
  pending: isProductsLoading,
  refresh: refreshProduct,
} = await useAsyncData(
  `get products by category - ${route.params.id} and ${link.value}`,
  () =>
    $get(`/api/v1/product/get-by-category/${route.params.id}`, {
      params: {
        limit: !limit.value ? 30 : limit.value,
        page: isMoreDetails.value ? 1 : page.value,
        sort: sort.value,
        minPrice: storeFilters.value?.minPrice,
        maxPrice: storeFilters.value?.maxPrice,
        filter: storeFilters.value?.filter
          ? filterUrlTransformer.transformAttrMapToUrl(
              storeFilters.value?.filter
            )
          : null,
        rangeFilter: storeFilters.value?.range
          ? filterUrlTransformer.transformAttrMapToUrl(
              storeFilters.value?.range
            )
          : null,
      },
    }),
  { watch: [sort, page], default: () => [] }
);

const {
  data: totalProducts,
  pending: isPendingTotalProducts,
  refresh: refreshTotalProducts,
} = await useAsyncData(
  "get total products",
  () =>
    $get(`/api/v1/product/get/count/with-filter/${route.params.id}`, {
      params: {
        minPrice: storeFilters.value?.minPrice,
        maxPrice: storeFilters.value?.maxPrice,
        filter: storeFilters.value?.filter
          ? filterUrlTransformer.transformAttrMapToUrl(
              storeFilters.value?.filter
            )
          : null,
        rangeFilter: storeFilters.value?.range
          ? filterUrlTransformer.transformAttrMapToUrl(
              storeFilters.value?.range
            )
          : null,
      },
    }),
  {
    default: () => 0,
  }
);

const {
  data: listFilters,
  pending: isPendingListFilters,
  refresh: refreshListFilters,
} = await useAsyncData(
  `get list filters by categoryId - ${route.params.id}`,
  () =>
    $get(`/api/v1/category/${route.params.id}/category-filters`, {
      params: {
        minPrice: storeFilters.value?.minPrice,
        maxPrice: storeFilters.value?.maxPrice,
        filter: storeFilters.value?.filter
          ? filterUrlTransformer.transformAttrMapToUrl(
              storeFilters.value?.filter
            )
          : null,
        rangeFilter: storeFilters.value?.range
          ? filterUrlTransformer.transformAttrMapToUrl(
              storeFilters.value?.range
            )
          : null,
      },
    }),
  {
    default: () => [],
  }
);

async function apply() {
  drawerVisible.value = false;
  page.value = 1;
  // await navigateTo(
  //   `${link.value}&sort=${sort.value}&mode=${mode.value}&page=${page.value}`
  // );
  refreshTotalProducts();
  refreshProduct();
  refreshListFilters();
  scrollToTop();
}

const totalPages = computed(() => Math.ceil(totalProducts.value / perPage));

const { data: state } = await useAsyncData(
  `get category, filters, rangeFilter product by categoryId-${route.params.id}`,
  async () => {
    const [category, rangePrice] = await Promise.all([
      $get(`/api/v1/category/${route.params.id}`),
      $get(`/api/v1/product/${route.params.id}/get-range-price`),
    ]);

    return {
      category,
      rangePrice,
    };
  }
);

const storeFilterPrice = computed({
  get() {
    let price = [
      parseFloat(storeFilters.value.minPrice ?? state.value.rangePrice.min),
      parseFloat(storeFilters.value.maxPrice ?? state.value.rangePrice.max),
    ];

    return price;
  },
  set(value) {
    if (value.length) {
      storeFilters.value.minPrice = value[0];
      storeFilters.value.maxPrice = value[1];
    } else {
      storeFilters.value.minPrice = null;
      storeFilters.value.maxPrice = null;
    }
  },
});

function onSelectPrice(value) {
  storeFilterPrice.value = value;
}

async function reset() {
  drawerVisible.value = false;
  storeFiltersValues.value = [];
  storeFilterPrice.value = [];
  page.value = 1;
  moreButtonClickCount.value = 1;
  refreshTotalProducts();
  refreshProduct();
  refreshListFilters();
}
</script>
<template>
  <div class="w-full pb-10 pt-4">
    <h2
      class="text-2xl lg:text-6xl font-semibold pb-4 lg:pb-6"
      v-if="state && state.category"
    >
      {{ state.category.name }}
    </h2>
    <div class="flex flex-row gap-5">
      <div class="w-70 hidden lg:block">
        <p-product-filter
          :filters="listFilters"
          v-model="storeFiltersValues"
          :minPrice="parseFloat(state.rangePrice.min)"
          :maxPrice="parseFloat(state.rangePrice.max)"
          :filterPrice="storeFilterPrice"
          @apply="apply"
          @reset="reset"
          @on-select-price="onSelectPrice"
        ></p-product-filter>
      </div>

      <div class="w-full flex flex-col space-y-4">
        <div class="flex flex-row justify-between items-center">
          <p-product-filter-header
            @change-mode="changeMode"
            @change-sort="changeSort"
          ></p-product-filter-header>
          <button
            @click="drawerVisible = true"
            class="lg:hidden cursor-pointer flex flex-row space-x-2 items-center font-lato text-primary-200 text-base"
          >
            <span>Фильтр</span>
            <p-icon-controls class="w-4 h-4"></p-icon-controls>
          </button>
        </div>
        <div>
          <p-list-products
            :products="products"
            :mode="mode"
            :loading="isProductsLoading"
            :totalPages="totalPages"
            @reset="reset"
          ></p-list-products>

          <p-pagination
            v-if="totalPages > 1"
            v-model="page"
            @moreLoad="more"
            class="mt-8 mx-auto"
            position="center"
            :length="totalPages"
          ></p-pagination>
        </div>
      </div>
    </div>

    <p-drawer :is-open="drawerVisible" :speed="500" :max-Width="`100%`">
      <div class="w-full px-4 pb-6">
        <p
          class="font-lato flex flex-row items-center space-x-2 text-xl font-medium pt-6 pb-4"
        >
          <p-icon-long-arrow
            @click="drawerVisible = false"
            class="rotate-180 w-7 cursor-pointer"
          ></p-icon-long-arrow>
          <span>Фильтры </span>
        </p>
        <p-product-filter
          :filters="listFilters"
          v-model="storeFiltersValues"
          :productLength="products.length"
          :minPrice="parseFloat(state.rangePrice.min)"
          :maxPrice="parseFloat(state.rangePrice.max)"
          :filterPrice="storeFilterPrice"
          @apply="apply"
          @reset="reset"
          @on-select-price="onSelectPrice"
        ></p-product-filter>
      </div>
    </p-drawer>
  </div>
</template>
